import { motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { AppPage } from '@hooks/useNavigation';
import { useOptionsData } from '@contexts/OptionsDataContext';
import { usePrices } from '@hooks/usePrices';
import {
  fetchExtendedPriceHistory,
  calculateOptionPriceHistory,
  calculateOptionPriceAtPoint,
} from '@services/optionDiscoveryService';
import { createOptionIdentifier } from '@/types/optionDiscovery';
import type { OptionPricePoint } from '@/types/optionDiscovery';
import { brandColors } from '@utils/color-utils';
import { volatilityService } from '@services/volatilityService';
import { getCurrentTime } from '@services/timeshiftService';

import { OptionPriceChart } from './options/OptionPriceChart';

const SOL_TEXT = '$SOL';
const JOYRIDE_TEXT = 'Options on Joyride';
const TRANSITION_DELAY_MS = 3000; // 3 seconds before transitioning to option price

interface SplashScreenProps {
  onNavigate: (page: AppPage) => void;
}

export function SplashScreen({ onNavigate }: SplashScreenProps) {
  // Preload options data while on splash screen
  const { options: allOptions } = useOptionsData();

  const [solPriceHistory, setSolPriceHistory] = useState<OptionPricePoint[]>([]);
  const [optionPriceHistory, setOptionPriceHistory] = useState<OptionPricePoint[]>([]);
  const [isLoadingChart, setIsLoadingChart] = useState(true);
  const [showOptionPrice, setShowOptionPrice] = useState(false);
  const [showOptionPriceDot, setShowOptionPriceDot] = useState(false);
  const [priceDirection, setPriceDirection] = useState<'up' | 'down'>('up');
  const [visibleChars, setVisibleChars] = useState(0);
  const [visibleJoyrideChars, setVisibleJoyrideChars] = useState(0);
  const [animatedPerformance, setAnimatedPerformance] = useState<number | null>(null);
  const [animatedSolPerformance, setAnimatedSolPerformance] = useState<number | null>(null);
  const prevPriceRef = useRef<number | null>(null);
  const hasAnimatedPerformanceRef = useRef(false);
  const hasAnimatedSolPerformanceRef = useRef(false);
  const performanceAnimationRef = useRef<number | null>(null);
  const currentPerformanceRef = useRef<number>(0);
  const livePricesRef = useRef<Map<string, { price: number }> | undefined>(undefined);
  const performanceIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const featuredOptionRef = useRef<typeof allOptions[0] | null>(null);
  const pricesQuery = usePrices();
  const livePrices = pricesQuery.data;
  
  // Keep ref updated with latest prices
  useEffect(() => {
    livePricesRef.current = livePrices;
  }, [livePrices]);

  // Fetch SOL price history
  useEffect(() => {
    let cancelled = false;

    const fetchSolHistory = async () => {
      try {
        const history = await fetchExtendedPriceHistory('SOL');
        if (cancelled) return;

        // Convert SecondPricePoint[] to OptionPricePoint[] format
        const optionPriceHistory: OptionPricePoint[] = history.map(point => ({
          timestamp: point.timestamp,
          price: point.price,
          spotPrice: point.price,
          volatility: 0, // Not needed for display
        }));

        setSolPriceHistory(optionPriceHistory);
        setIsLoadingChart(false);
      } catch (err) {
        console.error('Error fetching SOL price history:', err);
        if (!cancelled) {
          setIsLoadingChart(false);
        }
      }
    };

    fetchSolHistory();

    return () => {
      cancelled = true;
    };
  }, []);

  // Track price direction
  const currentSolPrice = useMemo(() => {
    if (solPriceHistory.length === 0) {
      return null;
    }
    const livePrice = livePrices instanceof Map ? livePrices.get('SOL')?.price : undefined;
    return livePrice ?? solPriceHistory[solPriceHistory.length - 1].price;
  }, [solPriceHistory, livePrices]);

  useEffect(() => {
    if (currentSolPrice === null) return;

    if (prevPriceRef.current === null) {
      prevPriceRef.current = currentSolPrice;
      return;
    }

    if (currentSolPrice !== prevPriceRef.current) {
      setPriceDirection(currentSolPrice > prevPriceRef.current ? 'up' : 'down');
      prevPriceRef.current = currentSolPrice;
    }
  }, [currentSolPrice]);

  // Get best performing SOL option with performance <= 100%
  const bestOption = useMemo(() => {
    if (allOptions.length === 0) {
      return null;
    }
    // Filter to only SOL options with performance <= 100%
    const solOptions = allOptions
      .filter(opt => opt.asset === 'SOL' && opt.priceChangePercent <= 110)
      .sort((a, b) => b.priceChangePercent - a.priceChangePercent); // Sort descending by performance
    
    return solOptions.length > 0 ? solOptions[0] : null;
  }, [allOptions]);

  // Store the initially selected option and keep it consistent
  // This prevents the featured option from changing mid-session
  useEffect(() => {
    if (bestOption && !featuredOptionRef.current) {
      // Only set once - when we first have a valid option
      featuredOptionRef.current = bestOption;
    }
  }, [bestOption]);

  // Use the stored featured option for display, falling back to bestOption if not set yet
  const featuredOption = featuredOptionRef.current || bestOption;

  // Calculate option price history for featured option
  useEffect(() => {
    if (!featuredOption || solPriceHistory.length === 0) {
      return;
    }

    const calculateOptionHistory = async () => {
      try {
        // Get the spot price history for the option's asset
        const spotHistory = await fetchExtendedPriceHistory(featuredOption.asset);
        
        // Create option identifier
        const optionId = createOptionIdentifier(
          featuredOption.asset,
          featuredOption.strikePrice,
          featuredOption.type,
          featuredOption.expiryTimestamp,
        );

        // Calculate option price history
        const optionHistory = calculateOptionPriceHistory(optionId, spotHistory);
        setOptionPriceHistory(optionHistory);
      } catch (err) {
        console.error('Error calculating option price history:', err);
      }
    };

    calculateOptionHistory();
  }, [featuredOption, solPriceHistory.length]);

  // Transition to option price after delay
  useEffect(() => {
    if (optionPriceHistory.length > 0 && !showOptionPrice) {
      const timer = setTimeout(() => {
        // Immediately show option price line (no fade out)
        setShowOptionPrice(true);
        // Show pulsing dot after line animation completes (1 second)
        setTimeout(() => {
          setShowOptionPriceDot(true);
        }, 1000); // 1 second delay for line animation
      }, TRANSITION_DELAY_MS);

      return () => clearTimeout(timer);
    }
  }, [optionPriceHistory.length, showOptionPrice]);

  // Create spot price chart data
  const spotChartData = useMemo(() => {
    if (solPriceHistory.length === 0) {
      return [];
    }
    return solPriceHistory.map(p => ({
      ...p,
      price: p.spotPrice,
    }));
  }, [solPriceHistory]);

  // Create option price chart data
  const optionChartData = useMemo(() => {
    if (optionPriceHistory.length === 0) {
      return [];
    }
    return optionPriceHistory;
  }, [optionPriceHistory]);

  // Calculate live option price for real-time performance updates
  const liveOptionPrice = useMemo(() => {
    if (!featuredOption || optionChartData.length === 0) {
      return null;
    }

    // Get live spot price
    const liveSpotPrice = livePrices instanceof Map ? livePrices.get(featuredOption.asset)?.price : undefined;
    if (!liveSpotPrice || liveSpotPrice <= 0) {
      // Fallback to last chart point
      return optionChartData[optionChartData.length - 1].price;
    }

    // Get current volatility
    const currentVolatility = volatilityService.getVolatility(featuredOption.asset);
    if (!currentVolatility || currentVolatility <= 0) {
      // Fallback to last chart point volatility
      const lastPoint = optionChartData[optionChartData.length - 1];
      return lastPoint.price;
    }

    // Calculate live option price
    const optionId = createOptionIdentifier(
      featuredOption.asset,
      featuredOption.strikePrice,
      featuredOption.type,
      featuredOption.expiryTimestamp,
    );

    const livePrice = calculateOptionPriceAtPoint(
      optionId,
      liveSpotPrice,
      currentVolatility,
      getCurrentTime(),
    );

    return livePrice > 0 ? livePrice : optionChartData[optionChartData.length - 1].price;
  }, [featuredOption, livePrices, optionChartData]);

  // Typewriter effect for SOL text - wait 1 second after chart is drawn (only runs once)
  const hasStartedTypewriter = useRef(false);
  
  useEffect(() => {
    if (!isLoadingChart && spotChartData.length > 0 && !hasStartedTypewriter.current) {
      hasStartedTypewriter.current = true;
      
      // Wait 1 second after the chart line finishes drawing (1s animation + 1s delay = 2s total)
      let intervalId: NodeJS.Timeout | null = null;
      
      const delayTimer = setTimeout(() => {
        setVisibleChars(0);
        let currentIndex = 0;
        intervalId = setInterval(() => {
          if (currentIndex < SOL_TEXT.length) {
            currentIndex++;
            setVisibleChars(currentIndex);
          } else {
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          }
        }, 150); // 150ms per character
      }, 1000); // 1 second delay after chart animation completes

      return () => {
        clearTimeout(delayTimer);
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }
  }, [isLoadingChart, spotChartData.length]);

  // Typewriter effect for "Options on Joyride" text - starts after option price line finishes drawing (1s delay)
  const hasStartedJoyrideTypewriter = useRef(false);
  const [showGoButton, setShowGoButton] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (showOptionPrice && !hasStartedJoyrideTypewriter.current && optionPriceHistory.length > 0) {
      // Wait 1 second for the line animation to complete before starting text animation
      const timer = setTimeout(() => {
        hasStartedJoyrideTypewriter.current = true;
        
        setVisibleJoyrideChars(0);
        let currentIndex = 0;
        intervalRef.current = setInterval(() => {
          if (currentIndex < JOYRIDE_TEXT.length) {
            currentIndex++;
            setVisibleJoyrideChars(currentIndex);
          } else {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            // Show Go button after animation completes
            setShowGoButton(true);
          }
        }, 100); // 100ms per character
      }, 1000); // 1 second delay after line animation completes

      return () => {
        clearTimeout(timer);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  }, [showOptionPrice, optionPriceHistory.length]);

  const handleGo = () => {
    onNavigate('optionshome');
  };

  // Format option label (e.g., "SOL ABOVE $140 Today")
  const optionLabel = useMemo(() => {
    if (!featuredOption) return '';
    const direction = featuredOption.type === 'CALL' ? 'ABOVE' : 'BELOW';
    const strikeFormatted = featuredOption.strikePrice.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return `${featuredOption.asset} ${direction} $${strikeFormatted} Today`;
  }, [featuredOption]);

  // Calculate and animate performance when option price line starts drawing
  // Then update every second as the option price changes
  useEffect(() => {
    if (!showOptionPrice || !featuredOption || optionChartData.length === 0) {
      return;
    }

    const inceptionPrice = featuredOption.inceptionPrice;
    if (inceptionPrice <= 0) {
      return;
    }

    // Helper function to calculate current live option price
    // Uses ref to always get fresh prices
    const getCurrentLivePrice = (): number | null => {
      // Get live spot price from ref (always fresh)
      const currentLivePrices = livePricesRef.current;
      const liveSpotPrice = currentLivePrices?.get(featuredOption.asset)?.price;
      if (!liveSpotPrice || liveSpotPrice <= 0) {
        // Fallback to last chart point
        return optionChartData[optionChartData.length - 1]?.price ?? null;
      }

      // Get current volatility
      const currentVolatility = volatilityService.getVolatility(featuredOption.asset);
      if (!currentVolatility || currentVolatility <= 0) {
        // Fallback to last chart point volatility
        return optionChartData[optionChartData.length - 1]?.price ?? null;
      }

      // Calculate live option price
      const optionId = createOptionIdentifier(
        featuredOption.asset,
        featuredOption.strikePrice,
        featuredOption.type,
        featuredOption.expiryTimestamp,
      );

      const livePrice = calculateOptionPriceAtPoint(
        optionId,
        liveSpotPrice,
        currentVolatility,
        getCurrentTime(),
      );

      return livePrice > 0 ? livePrice : optionChartData[optionChartData.length - 1]?.price ?? null;
    };

    // If this is the first time (initial animation), animate from 0
    if (!hasAnimatedPerformanceRef.current) {
      hasAnimatedPerformanceRef.current = true;
      
      // Use current live price for initial calculation
      const currentPrice = getCurrentLivePrice();
      if (!currentPrice || currentPrice <= 0) {
        return;
      }

      // Calculate current performance percentage
      const currentPerformance = ((currentPrice - inceptionPrice) / inceptionPrice) * 100;
      
      // Animation duration is 1 second (matches line drawing animation)
      const animationDuration = 1000;
      const startTime = Date.now();
      
      // Start animation immediately when line starts drawing
      setAnimatedPerformance(0);
      
      const updatePerformance = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / animationDuration, 1);
        
        // Smoothly interpolate from 0% to current performance percentage
        const performance = currentPerformance * progress;
        setAnimatedPerformance(performance);
        
        // Continue updating until animation completes
        if (progress < 1) {
          requestAnimationFrame(updatePerformance);
        } else {
          // Final value - set to the current performance
          setAnimatedPerformance(currentPerformance);
          currentPerformanceRef.current = currentPerformance;
        }
      };
      
      requestAnimationFrame(updatePerformance);
    }

    // After initial animation, update every second
    // Use a ref to track if interval is set up to avoid recreating it
    if (!performanceIntervalRef.current) {
      performanceIntervalRef.current = setInterval(() => {
        // Recalculate live price each time to get fresh values
        const currentPrice = getCurrentLivePrice();
        if (!currentPrice || currentPrice <= 0) {
          return;
        }

        // Calculate current performance percentage
        const targetPerformance = ((currentPrice - inceptionPrice) / inceptionPrice) * 100;
        
        // Get current performance from ref (always fresh)
        const previousPerformance = currentPerformanceRef.current;
        
        // Only update if there's a meaningful change (avoid jitter)
        if (Math.abs(targetPerformance - previousPerformance) > 0.01) {
          // Cancel any ongoing animation
          if (performanceAnimationRef.current !== null) {
            cancelAnimationFrame(performanceAnimationRef.current);
            performanceAnimationRef.current = null;
          }
          
          // Start smooth animation
          const animationDuration = 300; // 300ms for smooth updates
          const startTime = Date.now();
          const startPerformance = previousPerformance;
          
          const updatePerformance = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / animationDuration, 1);
            
            // Smooth interpolation
            const performance = startPerformance + (targetPerformance - startPerformance) * progress;
            setAnimatedPerformance(performance);
            currentPerformanceRef.current = performance;
            
            if (progress < 1) {
              performanceAnimationRef.current = requestAnimationFrame(updatePerformance);
            } else {
              setAnimatedPerformance(targetPerformance);
              currentPerformanceRef.current = targetPerformance;
              performanceAnimationRef.current = null;
            }
          };
          
          performanceAnimationRef.current = requestAnimationFrame(updatePerformance);
        } else if (Math.abs(targetPerformance - previousPerformance) > 0.001) {
          // Small change, update immediately without animation
          setAnimatedPerformance(targetPerformance);
          currentPerformanceRef.current = targetPerformance;
        }
      }, 1000); // Update every second
    }

    return () => {
      if (performanceIntervalRef.current) {
        clearInterval(performanceIntervalRef.current);
        performanceIntervalRef.current = null;
      }
      if (performanceAnimationRef.current !== null) {
        cancelAnimationFrame(performanceAnimationRef.current);
        performanceAnimationRef.current = null;
      }
    };
  }, [showOptionPrice, featuredOption, optionChartData.length, featuredOption?.asset, featuredOption?.strikePrice, featuredOption?.type, featuredOption?.expiryTimestamp]);

  // Calculate and animate SOL performance when chart is drawn
  useEffect(() => {
    if (!isLoadingChart && spotChartData.length > 0 && !hasAnimatedSolPerformanceRef.current) {
      const firstPrice = spotChartData[0].price;
      const lastPrice = spotChartData[spotChartData.length - 1].price;
      
      if (firstPrice <= 0 || lastPrice <= 0) {
        return;
      }

      // Calculate final performance percentage
      const finalPerformance = ((lastPrice - firstPrice) / firstPrice) * 100;
      
      // Animation duration is 1 second (matches line drawing animation)
      const animationDuration = 1000;
      const startTime = Date.now();
      
      // Mark that we've started animating
      hasAnimatedSolPerformanceRef.current = true;
      
      // Start animation immediately when chart starts drawing
      setAnimatedSolPerformance(0);
      
      const updatePerformance = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / animationDuration, 1);
        
        // Smoothly interpolate from 0% to final performance percentage
        const performance = finalPerformance * progress;
        setAnimatedSolPerformance(performance);
        
        // Continue updating until animation completes
        if (progress < 1) {
          requestAnimationFrame(updatePerformance);
        } else {
          // Final value
          setAnimatedSolPerformance(finalPerformance);
        }
      };
      
      requestAnimationFrame(updatePerformance);
    }
  }, [isLoadingChart, spotChartData]);

  // Format performance percentage
  const performanceText = useMemo(() => {
    if (animatedPerformance === null) return '';
    const sign = animatedPerformance >= 0 ? '+' : '';
    return `${sign}${animatedPerformance.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}%`;
  }, [animatedPerformance]);

  // Format SOL performance percentage
  const solPerformanceText = useMemo(() => {
    if (animatedSolPerformance === null) return '';
    const sign = animatedSolPerformance >= 0 ? '+' : '';
    return `${sign}${animatedSolPerformance.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}% Today`;
  }, [animatedSolPerformance]);

  return (
    <div
      className="h-full w-full relative"
      style={{ backgroundColor: 'var(--brand-dark)' }}
    >

      {/* Layout with flexbox: text, charts, button */}
      <div className="h-full w-full flex flex-col">
        {/* Animated SOL text at top - always reserves space */}
        <div className="flex-shrink-0 pt-8 pb-4 flex flex-col items-center" style={{ minHeight: '120px' }}>
          {!isLoadingChart && spotChartData.length > 0 && (
            <>
              <div className="text-white text-2xl md:text-3xl font-bold">
                {SOL_TEXT.slice(0, visibleChars)}
              </div>
              {/* Options on Joyride text - typewriter effect when option price shows */}
              {showOptionPrice && (
                <div className="text-white text-2xl md:text-3xl font-bold mt-2">
                  {JOYRIDE_TEXT.slice(0, visibleJoyrideChars)}
                </div>
              )}
            </>
          )}
        </div>

        {/* Price Charts - fills remaining space */}
        <div className="flex-1 min-h-0 w-full flex flex-col items-center justify-center relative">
          {/* Spot price chart - 33% of option chart height, disappears when option price shows */}
          {!isLoadingChart && spotChartData.length > 0 && !showOptionPrice && (
            <div className="w-full absolute" style={{ height: '33%' }}>
              <OptionPriceChart
                priceHistory={spotChartData}
                priceDirection={priceDirection}
                showTimeRange={false}
                animate={true}
                showCurrentPrice={false}
                lineColor="#FFFFFF"
              />
            </div>
          )}

          {/* Option price chart - fills remaining space, animates left to right */}
          {showOptionPrice && optionChartData.length > 0 && (
            <motion.div
              className="w-full absolute inset-0"
            >
              <OptionPriceChart
                priceHistory={optionChartData}
                priceDirection={priceDirection}
                showTimeRange={false}
                animate={true}
                showCurrentPrice={showOptionPriceDot}
                zoomLevel={1}
                panOffset={0}
                lineColor={brandColors.green}
              />
            </motion.div>
          )}
        </div>

        {/* SOL performance below chart - same position as option performance */}
        {!showOptionPrice && animatedSolPerformance !== null && (
          <div className="flex-shrink-0 pb-2 flex flex-col items-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="text-sm font-medium"
              style={{ color: '#FFFFFF' }}
            >
              {solPerformanceText}
            </motion.div>
          </div>
        )}

        {/* Option label below chart - appears when green chart appears */}
        {showOptionPrice && featuredOption && (
          <div className="flex-shrink-0 pb-2 flex flex-col items-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="text-sm font-medium"
              style={{ color: brandColors.green }}
            >
              {optionLabel}
            </motion.div>
            {/* Performance - appears at the same time as label when green chart appears */}
            {animatedPerformance !== null && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="text-sm font-medium mt-1"
                style={{ color: brandColors.green }}
              >
                {performanceText}
              </motion.div>
            )}
          </div>
        )}

        {/* Go Button - always reserves space */}
        <div className="flex-shrink-0 px-6 py-6" style={{ minHeight: '88px' }}>
          {showGoButton && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="w-full px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              style={{
                backgroundColor: '#3C52FF',
                color: '#FFF',
              }}
              onClick={handleGo}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Go
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
}

